import React, { useState, useMemo, useEffect } from 'react';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import _without from 'lodash/without';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import reorderItems from 'helpers/reorderItems';
import { Theme } from '@material-ui/core/styles';
import { Field } from '@sift/types';
import * as Types from 'pages/Features/typings';
import { MAX_VISIBLE_CUSTOM_ATTRIBUTES } from 'pages/Features/constants';

import Typography from '@material-ui/core/Typography';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import ReorderIcon from '@material-ui/icons/Reorder';

interface Props {
  fields: Field[];
  values: Types.FeatureValues;
  view: string;
  onChange: (field: string, value: any) => void;
}

function CustomAttrs({ fields, values, view, onChange }: Props) {
  const classes = useStyles();
  const keyedFields = useMemo(() => _keyBy(fields, 'objectKey'), [fields]);

  const layoutValue = _get(values, ['layouts', 'org-chart-custom-attributes']);
  const [layout, setLayout] = useState(layoutValue);
  const [dragging, setDragging] = useState(false);

  const visibleCustomAttributeKeys = _get(
    values,
    ['person', 'directory', 'orgChart', 'cardContent', view, 'customAttributes'],
    [],
  );

  const customAttrFieldOptions = layout.map((item: Types.LayoutItem) => item.name);
  const validVisibleCustomAttributeKeys = visibleCustomAttributeKeys.filter((k: string) =>
    customAttrFieldOptions.includes(k),
  );

  const hiddenCustomAttributeKeys = customAttrFieldOptions.filter(
    (k: string) => !validVisibleCustomAttributeKeys.includes(k),
  );

  useEffect(() => {
    setLayout(layoutValue);
  }, [layoutValue]);

  const renderHeader = () => {
    return (
      <>
        <Typography variant="h6">Additional Attributes</Typography>
        <Typography variant="body2">
          Select additional attributes to be shown in the middle of the card. You may select up to
          10 attributes.
        </Typography>
      </>
    );
  };

  if (!layout.length) {
    return (
      <div className={classes.root}>
        {renderHeader()}
        <div className={classes.emptyState}>
          <Typography variant="body2">There are currently no custom attributes to show</Typography>
        </div>
      </div>
    );
  }

  const handleDragStart = () => {
    setDragging(true);
  };

  const handleDragEnd = (result: DropResult) => {
    setDragging(false);

    const updatedVisibleAttrs = reorderItems(result, validVisibleCustomAttributeKeys);
    if (!updatedVisibleAttrs) return;

    return onChange(
      `person.directory.orgChart.cardContent.${view}.customAttributes`,
      updatedVisibleAttrs,
    );
  };

  const handleToggle = (objectKey: string) => {
    let attrKeys = [...validVisibleCustomAttributeKeys];

    if (attrKeys.includes(objectKey)) {
      attrKeys = _without(attrKeys, objectKey);
    } else {
      attrKeys.push(objectKey);
    }

    return onChange(`person.directory.orgChart.cardContent.${view}.customAttributes`, attrKeys);
  };

  return (
    <div className={classes.root}>
      {renderHeader()}
      <FormGroup className={classes.formContainer}>
        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <Droppable droppableId="sortable-column-list">
            {(provided: any) => (
              <FormGroup ref={provided.innerRef} {...provided.droppableProps}>
                {validVisibleCustomAttributeKeys.map((attrKey: string, index: number) => {
                  const field = keyedFields[attrKey];
                  const fieldDisplayName = field.displayName;

                  return (
                    <Draggable
                      draggableId={`${attrKey}-${index}`}
                      index={index}
                      key={`${attrKey}-${index}`}
                    >
                      {provided => (
                        <span
                          ref={provided.innerRef}
                          className={classes.draggableItem}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={validVisibleCustomAttributeKeys.includes(attrKey)}
                                name={fieldDisplayName}
                                onChange={() => handleToggle(attrKey)}
                                aria-label="ios-switch-on"
                                className={classes.switchMargin}
                              />
                            }
                            label={<span className={classes.label}>{fieldDisplayName}</span>}
                          />
                          <ReorderIcon className={classes.reorderIcon} />
                        </span>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </FormGroup>
            )}
          </Droppable>
        </DragDropContext>
        <FormGroup
          style={{
            maxHeight: '40vh',
            flexWrap: 'nowrap',
            overflow: dragging ? 'hidden' : 'auto',
            marginTop: dragging ? 5 : 0,
          }}
        >
          {hiddenCustomAttributeKeys.map((attrKey: string, index: number) => {
            const field = keyedFields[attrKey];
            const fieldDisplayName = field.displayName;

            return (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={validVisibleCustomAttributeKeys.includes(attrKey)}
                    name={fieldDisplayName}
                    onChange={() => handleToggle(attrKey)}
                    aria-label="switch-on"
                    className={classes.switchMargin}
                  />
                }
                label={<span className={classes.label}>{fieldDisplayName}</span>}
                key={`${attrKey}-${index}`}
                className={classes.unselected}
                disabled={validVisibleCustomAttributeKeys.length >= MAX_VISIBLE_CUSTOM_ATTRIBUTES}
              />
            );
          })}
        </FormGroup>
      </FormGroup>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
    width: '100%',
    height: '100%',
    maxWidth: 372,
    overflowY: 'auto',
    [theme.breakpoints.down('lg')]: {
      maxWidth: 336,
    },
  },
  formContainer: {
    marginTop: theme.spacing(),
  },
  draggableItem: {
    display: 'flex',
    alignItems: 'center',
    height: 40,
  },
  switchMargin: {
    margin: theme.spacing(1.5),
  },
  reorderIcon: {
    marginLeft: 'auto',
  },
  unselected: {
    minHeight: 40,
  },
  emptyState: {
    marginTop: theme.spacing(11),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(5),
    },
  },
  label: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export default CustomAttrs;
